import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Plus, ClipboardCheck, Send, Info } from 'lucide-react';
import { performanceService } from '../../services/performanceService';
import { employeeService } from '../../services/employeeService';
import { useAuth } from '../../context/AuthContext';
import StatusBadge from '../../components/StatusBadge';

const CYCLES = ['Monthly', 'Quarterly', 'Annual'];

// ===== Modal for Creating Review Cycle =====
const NewCycleModal = ({ onClose, onCreated }) => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState('');
  const [cycle, setCycle] = useState('Quarterly');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [kpis, setKpis] = useState([{ kpi_name: '', description: '' }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    employeeService.getEmployees(undefined, 'active').then(res => setEmployees(res.data || []));
  }, []);

  const addKpi = () => setKpis([...kpis, { kpi_name: '', description: '' }]);
  
  const updateKpi = (index, field, value) => {
    const updated = kpis.map((k, i) => {
      if (i === index) return { ...k, [field]: value };
      return k;
    });
    setKpis(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedEmp || !start || !end) {
      setError('Please fill required fields');
      return;
    }
    setLoading(true);
    try {
      await performanceService.createReview({
        employee_id: selectedEmp,
        cycle,
        period_start: start,
        period_end: end,
        kpis: kpis.filter(k => k.kpi_name)
      });
      onCreated();
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-card shadow-elevated w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Start Review Cycle</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && <div className="p-3 bg-red-50 text-red-700 rounded text-sm">{error}</div>}
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Employee</label>
              <select value={selectedEmp} onChange={(e) => setSelectedEmp(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:ring-1 focus:ring-primary outline-none">
                <option value="">Select Employee</option>
                {employees.map(emp => (
                  <option key={emp.employee_id} value={emp.employee_id}>{emp.full_name} ({emp.employee_id})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Cycle</label>
              <select value={cycle} onChange={(e) => setCycle(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:ring-1 focus:ring-primary outline-none">
                {CYCLES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Period Start</label>
              <input type="date" value={start} onChange={(e) => setStart(e.target.value)} max="2099-12-31" className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:ring-1 focus:ring-primary outline-none" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Period End</label>
              <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} max="2099-12-31" className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:ring-1 focus:ring-primary outline-none" />
            </div>
          </div>

          <div className="space-y-3 pt-2">
            <div className="flex items-center justify-between border-b pb-1">
              <label className="text-sm font-semibold text-gray-700">KPIs & Goals</label>
              <button type="button" onClick={addKpi} className="text-xs text-primary font-bold hover:underline">+ Add KPI</button>
            </div>
            <div className="space-y-3 max-h-48 overflow-y-auto pr-1">
              {kpis.map((k, idx) => (
                <div key={idx} className="space-y-2 p-3 bg-gray-50 rounded-lg border border-gray-100 relative">
                  <input 
                    placeholder="KPI Name (e.g. Sales Target, Code Quality)" 
                    value={k.kpi_name} 
                    onChange={(e) => updateKpi(idx, 'kpi_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:ring-1 focus:ring-primary outline-none bg-white"
                  />
                  <textarea 
                    placeholder="Brief description of expectations..." 
                    value={k.description} 
                    onChange={(e) => updateKpi(idx, 'description', e.target.value)}
                    className="w-full px-3 py-1.5 border border-gray-200 rounded text-xs focus:ring-1 focus:ring-primary outline-none bg-white"
                    rows={1}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t mt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900">Cancel</button>
            <button disabled={loading} className="bg-primary text-white px-6 py-2 rounded-control text-sm font-semibold flex items-center gap-2 hover:bg-primary-dark transition-colors shadow-lg shadow-primary/20">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Create Review Cycle
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ===== Main Performance Page Component =====
const Performance = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user && (user.role === 'super_admin' || user.role === 'hr_manager');
  
  const [activeTab, setActiveTab] = useState(isAdmin ? 'team' : 'self');
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = activeTab === 'team' 
        ? await performanceService.getAllReviews() 
        : await performanceService.getMyReviews();
      setReviews(res.data || []);
    } catch (err) {
      console.error("Data load error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [activeTab]);

  return (
    <div className="space-y-6 pb-10">
      {/* Page Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Performance Management</h1>
          <p className="text-sm text-gray-500">Track professional growth and feedback</p>
        </div>
        {isAdmin && (
          <button 
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 bg-primary text-white px-4 py-2.5 rounded-control text-sm font-semibold hover:bg-primary-dark transition-all shadow-md shadow-primary/20"
          >
            <Plus className="w-4 h-4" /> Start New Cycle
          </button>
        )}
      </div>

      {/* Navigation Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-100">
        {isAdmin && (
          <button 
            onClick={() => setActiveTab('team')}
            className={'px-6 py-3 text-sm font-semibold border-b-2 transition-colors ' + (activeTab === 'team' ? 'border-primary text-primary' : 'border-transparent text-gray-400 hover:text-gray-600')}
          >
            Team Reviews
          </button>
        )}
        <button 
          onClick={() => setActiveTab('self')}
          className={'px-6 py-3 text-sm font-semibold border-b-2 transition-colors ' + (activeTab === 'self' ? 'border-primary text-primary' : 'border-transparent text-gray-400 hover:text-gray-600')}
        >
          My Reviews
        </button>
      </div>

      {/* Reviews List */}
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-10 h-10 animate-spin text-primary opacity-20" /></div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {reviews.length === 0 ? (
            <div className="bg-white p-12 text-center rounded-card border border-dashed border-gray-200">
              <Info className="w-10 h-10 text-gray-200 mx-auto mb-3" />
              <p className="text-gray-400 font-medium">No performance records found.</p>
            </div>
          ) : (
            reviews.map(rev => (
              <div key={rev.id} className="bg-white p-5 rounded-card border shadow-soft flex items-center justify-between gap-4 hover:border-primary/30 transition-colors">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-gray-900">{rev.employee_name}</h3>
                    <span className="text-[10px] bg-gray-100 px-1.5 py-0.5 rounded font-mono text-gray-500 uppercase tracking-tighter">{rev.review_id}</span>
                  </div>
                  <p className="text-xs text-gray-500 flex items-center gap-1.5">
                    <span className="font-medium text-gray-700">{rev.cycle} Cycle</span> 
                    &bull; 
                    <span>{rev.period_start} to {rev.period_end}</span>
                  </p>
                  <div className="pt-1.5"><StatusBadge status={rev.status} /></div>
                </div>
                
                <div className="flex items-center gap-4">
                  {rev.final_rating && (
                    <div className="text-right hidden sm:block border-r pr-4 border-gray-100">
                      <p className="text-[10px] uppercase text-gray-400 font-bold tracking-widest">Final Rating</p>
                      <p className="text-sm font-black text-primary uppercase">{rev.final_rating}</p>
                    </div>
                  )}
                  {/* Updated Navigation Button */}
                  <button 
                    onClick={() => navigate(`/performance/${rev.review_id}`)}
                    className="p-2.5 text-gray-400 hover:text-primary hover:bg-primary/5 border border-gray-100 rounded-xl transition-all group"
                    title="View Assessment"
                  >
                    <ClipboardCheck className="w-6 h-6 group-hover:scale-110 transition-transform" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Modal Render */}
      {showModal && <NewCycleModal onClose={() => setShowModal(false)} onCreated={() => { setShowModal(false); loadData(); }} />}
    </div>
  );
};

const X = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
);

export default Performance;